import { definePreset } from "@primeuix/themes";
import Aura from "@primeuix/themes/aura";

const WaypointTheme = definePreset(Aura, {
  primitive: {
    spacing: {
      none: "0px",
      "2xs": "2px",
      "1xs": "4px",
      xs: "8px",
      sm: "12px",
      md: "16px",
      lg: "20px",
      xl: "24px",
      "3xl": "32px",
    },
    fontSize: {
      xs: "0.75rem",
      sm: "0.875rem",
      md: "1rem",
      base: "1rem",
      lg: "1.125rem",
      xl: "1.25rem",
      "2xl": "1.5rem",
      h1: "2rem",
      h2: "1.5rem",
      h3: "1.25rem",
      h4: "1.125rem",
      h5: "1rem",
    },
    fontWeight: {
      medium: "500",
      semibold: "600",
      bold: "700",
    },
    lineHeight: {
      sm: "1.375rem",
      base: "1.5rem",
      h5: "1.5rem",
      h4: "1.75rem",
      h3: "2rem",
      h2: "2.25rem",
      h1: "2.25rem",
    },
    blue: {
      50: "#F0F7FD",
      100: "#E0EFF9",
      200: "#BADBF3",
      300: "#7EB7E8",
      400: "#378FDB",
      500: "#2470AF",
      600: "#195D9B",
      700: "#144B78",
      800: "#143D64",
      900: "#153353",
      950: "#0E2137",
    },
    green: {
      50: "#F1FCEA",
      100: "#DCF7D6",
      200: "#BCEFB0",
      300: "#83E86F",
      400: "#46D02B",
      500: "#31991E",
      600: "#247D13",
      700: "#1E6412",
      800: "#1C4F13",
      900: "#174311",
      950: "#082407",
    },
    red: {
      50: "#FCEEEE",
      100: "#F9DADA",
      200: "#F7B9B9",
      300: "#EF8989",
      400: "#E24545",
      500: "#8E1C1C",
      600: "#6F2020",
      700: "#5D1919",
      800: "#491616",
      900: "#3F1717",
      950: "#230909",
    },
    brightGray: {
      50: "#F2F3F6",
      100: "#E2E5ED",
      200: "#BFC7D6",
      300: "#889AB3",
      400: "#516280",
      500: "#343E4F",
      600: "#29324A",
      700: "#222935",
      800: "#1E232D",
      900: "#1D212A",
      950: "#121318",
    },
    gray: {
      50: "#F5F5F6",
      100: "#E6E7E9",
      200: "#CECED4",
      300: "#ABB0B6",
      400: "#818591",
      500: "#717580",
      600: "#575A63",
      700: "#4B4D53",
      800: "#424348",
      900: "#3A3B3F",
      950: "#242428",
    },
    orange: {
      50: "#FFFAEB",
      100: "#FEF2C3",
      200: "#FDE581",
      300: "#FBD041",
      400: "#FABA14",
      500: "#E59209",
      600: "#D87605",
      700: "#A74F08",
      800: "#883A0D",
      900: "#713010",
      950: "#401806",
    },
    surface: {
      0: "#FFFFFF",
      50: "{gray.50}",
      100: "{gray.100}",
      200: "{gray.200}",
      300: "{gray.300}",
      400: "{gray.400}",
      500: "{gray.500}",
      600: "{gray.600}",
      700: "{gray.700}",
      800: "{gray.800}",
      900: "{gray.900}",
      950: "{brightGray.950}",
    },
    borderRadius: {
      xl: "10px",
    },
  },
  semantic: {
    primary: {
      50: "#F0F7FD",
      100: "#E0EFF9",
      200: "#BADBF3",
      300: "#7EB7E8",
      400: "#378FDB",
      500: "#2470AF",
      600: "#195D9B",
      700: "#144B78",
      800: "#143D64",
      900: "#153353",
      950: "#0E2137",
    },
    sidenav: {
      width: "240px",
      widthCollapsed: "64px",
    },
    interactiveRow: {
      height: "40px",
    },
    z: {
      nav: "1000",
      overlay: "999",
    },
    colorScheme: {
      light: {
        primary: {
          color: "{primary.500}",
          contrastColor: "#ffffff",
          hoverColor: "{primary.600}",
          activeColor: "{primary.700}",
        },
        surface: {
          ground: "{surface.50}",
        },
      },
      dark: {
        text: {
          color: "{brightGray.100}",
        },
        primary: {
          color: "{primary.400}",
          contrastColor: "{surface.950}",
          hoverColor: "{primary.300}",
          activeColor: "{primary.200}",
        },
        surface: {
          ground: "#09090b",
        },
        content: {
          background: "#18181b",
        },
      },
    },
  },
  components: {
    breadcrumb: {
      root: {
        padding: "0",
      },
    },
    button: {
      colorScheme: {
        light: {
          outlined: {
            primary: {
              borderColor: "{primary.500}",
            },
          },
        },
      },
    },
    tag: {
      css: () => `
[data-pc-name="tag"][data-pc-section="root"] {
  max-width: 100%;
}

[data-pc-name="tag"] [data-pc-section="label"] {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
`,
    },
    datatable: {
      css: ({ dt }) => `
[data-pc-name="datatable"] [data-pc-section="thead"] > tr > th,
[data-pc-name="datatable"] [data-pc-section="tbody"] > tr > td {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

[data-pc-name="datatable"] a {
  text-decoration: none;
  color: ${dt("primary.500")};
}

[data-pc-name="datatable"] a:hover {
  text-decoration: underline;
}

[data-pc-name="datatable"] [data-pc-section="tbody"] > tr:last-child > td {
  border-bottom: none;
}
`,
    },
    paginator: {
      root: {
        gap: "0",
      },
      navButton: {
        borderRadius: "0",
      },
      colorScheme: {
        light: {
          navButton: {
            color: "{primary.500}",
          },
          currentPageReport: {
            color: "{surface.400}",
          },
        },
      },
      css: ({ dt }) => `
[data-pc-name="paginator"] [data-pc-section="content"],
[data-pc-name="pcpaginator"] [data-pc-section="content"] {
  width: 100%;
}

[data-pc-group-section="pagebutton"],
[data-pc-section="page"] {
  border: 1px solid ${dt("primary.500")};
  margin-left: -1px;
}

[data-pc-name="paginator"] [data-pc-section="content"]:not(:has([data-pc-section="page"])) > [data-pc-group-section="pagebutton"] {
  border: 1px solid ${dt("primary.500")};
}

[data-pc-section="last"]:has(+ [data-pc-name="pcrowperpagedropdown"]),
[data-pc-section="last"]:has(+ [data-pc-section="current"]) {
  margin-right: auto;
}

.p-paginator-page-selected {
  border-bottom: 2px solid ${dt("primary.500")} !important;
}

[data-pc-section="current"] {
  margin: 0 4px;
}

[data-pc-section="prev"]::after {
  content: 'Prev';
}

[data-pc-section="next"]::before {
  content: 'Next';
}

[data-pc-section="prev"],
[data-pc-section="next"] {
  padding: 12px;
  font-weight: 600;
}

[data-pc-section="first"] {
  border-radius: ${dt("border.radius.md")} 0 0 ${dt("border.radius.md")};
}

[data-pc-section="last"] {
  border-radius: 0 ${dt("border.radius.md")} ${dt("border.radius.md")} 0;
}
    `,
    },
    menu: {
      css: () => `
.p-menu-item-link {
  color: inherit !important;
}
`,
    },
    form: {
      root: { borderRadius: "10px" },
    },
  },
});

export { WaypointTheme };
